import React, { useEffect, useState } from 'react'

import { Layout, Menu, Modal, Space, theme } from 'antd'
import App from '../../custom/app'
import Sider from 'antd/es/layout/Sider'
import { HomeOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import CoreReducer from '../utils/reducer'
import CoreEnums from '../utils/enums'
import { useDispatch, useSelector } from 'react-redux'
import { deviceDetect } from 'react-device-detect'
import authReducer from '../../custom/Auth/utils/reducer'
import { firebaseAuth } from '../..'
import { signOut } from 'firebase/auth'
import { handleError } from '../../custom/lib/utils'
import Router from '../../custom/Auth/components/router'
import { Content } from 'antd/es/layout/layout'
import coreReducer from '../utils/reducer'
import MobNavBar from './MobNavBar'
import { isPatient } from '../../custom/lib/profile-utils'
import { warnUnsavedChanges } from '../utils/utils'

const TabNavigation = (props) => {
  const dispatch = useDispatch()
  const CustomRootContent = props.state.rootContent || App
  const { token } = theme.useToken()
  const coreState = useSelector((state) => state.core)
  const authState = useSelector((state) => state.auth)

  const [collapsed, setCollapsed] = useState(false)

  // Initialize collapsed state from Redux store on component mount
  useEffect(() => {
    // Only set initial state if coreState is available
    if (coreState) {
      const shouldBeCollapsed = !coreState.sideBarExpanded
      if (collapsed !== shouldBeCollapsed) {
        setCollapsed(shouldBeCollapsed)
      }
    }
    // eslint-disable-next-line
  }, [coreState.sideBarExpanded]) // React to changes in Redux state

  useEffect(() => {
    // Dispatch the correct value to Redux instead of using switch statement
    dispatch(coreReducer.actions.setSideBarExpanded(!collapsed))
    // eslint-disable-next-line
  }, [collapsed])

  function getItem(label, key, icon, children, newItem) {
    let tmpLabel = label

    if (newItem) {
      tmpLabel = (
        <Space>
          {label}
          <sup style={{ fontSize: 13, color: 'yellow' }}>new</sup>
        </Space>
      )
    }

    if (children && children.length > 0) {
      for (const child of children) {
        let tmpChildLabel = child.label

        if (child.new) {
          tmpChildLabel = (
            <Space>
              {child.label}
              <sup style={{ fontSize: 13, color: 'yellow' }}>new</sup>
            </Space>
          )

          child.new = false
        }

        child.label = tmpChildLabel
      }
    }

    return {
      key,
      icon,
      children,
      label: tmpLabel
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut(firebaseAuth)
      dispatch(CoreReducer.actions.resetState())
      dispatch(CoreReducer.actions.enableDisableLeftMenu(false))
      dispatch(CoreReducer.actions.enableDisableRightMenu(false))
      dispatch(CoreReducer.actions.enableDisableTabs(false))
      dispatch(CoreReducer.actions.setRootContent(Router))
      dispatch(authReducer.actions.setUser(null))
      dispatch(authReducer.actions.setAgiliteUser(null))
      dispatch(authReducer.actions.setLoggedIn(false))
    } catch (e) {
      handleError(e, true)
    }
  }

  const handleCheckTabKey = () => {
    let key = null

    props.menuItems().forEach((item) => {
      if (item.children) {
        item.children.forEach((child) => {
          if (coreState.tabNavigation.activeKey.indexOf(child.key) > -1) {
            key = child.key
          }
        })
      } else {
        if (coreState.tabNavigation.activeKey.indexOf(item.key) > -1) {
          key = item.key
        }
      }
    })

    if (key) {
      return key
    } else {
      return coreState.tabNavigation.activeKey
    }
  }

  const filterMenuItems = () => {
    let filteredItems = []
    let menuItems = []

    const menuItemIsValid = (menuItem) => {
      let valid = true
      if (menuItem.additionalValidatiors && menuItem.additionalValidatiors.length > 0) {
        menuItem.additionalValidatiors.forEach((validator) => {
          valid = validator()
        })
      }
      return valid
    }

    props.menuItems().forEach((item) => {
      if (
        (item.visibleTo.includes(authState.agiliteUser?.extraData?.role?.type) ||
          item.visibleTo.includes(authState.agiliteUser?.extraData?.profession)) &&
        menuItemIsValid(item)
      ) {
        if (item.propInEntity && item.enabledProp) {
          if (coreState.entity[item.propInEntity][item.enabledProp]) {
            if (!coreState.hiddenLeftMenuItems.includes(item.key)) {
              filteredItems.push(item)
            }
          }
        } else {
          if (!coreState.hiddenLeftMenuItems.includes(item.key)) {
            filteredItems.push(item)
          }
        }
      }

      item.children = item.children?.filter((child) => {
        return (
          (child.visibleTo.includes(authState.agiliteUser?.extraData?.role?.type) ||
            item.visibleTo.includes(authState.agiliteUser?.extraData?.profession)) &&
          menuItemIsValid(child)
        )
      })
    })

    menuItems = filteredItems.map((item) => {
      return getItem(item.label, item.key, item.icon ? item.icon : <HomeOutlined />, item.children, item.new)
    })

    return menuItems
  }

  const isMobile = deviceDetect().isMobile

  // Calculate consistent width values with fallbacks
  const expandedWidth = 275
  const collapsedWidth = 80
  const sidebarWidth = coreState.sideBarExpanded ? expandedWidth : collapsedWidth

  return (
    <>
      <Layout
        style={{
          margin: 0,
          position: 'sticky',
          top: 0,
          zIndex: 5
        }}
      >
        {deviceDetect().isBrowser ? (
          // LEFT MENU (leftMenu) for Browsers
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={(value) => setCollapsed(value)}
            width={sidebarWidth}
            collapsedWidth={collapsedWidth}
            style={{
              borderRight: `1px ${token.colorBorderSecondary} solid`,
              overflow: 'auto',
              zIndex: 9,
              position: 'relative',
              transition: 'all 300ms ease',
              minWidth: collapsed ? collapsedWidth : expandedWidth,
              maxWidth: collapsed ? collapsedWidth : expandedWidth
            }}
            trigger={
              collapsed ? (
                <RightOutlined />
              ) : (
                <>
                  <LeftOutlined /> Collapse
                </>
              )
            }
          >
            <Menu
              onClick={(event) => {
                if (event.key === CoreEnums.tabKeys.SIGN_OUT) {
                  Modal.confirm({
                    title: 'Confirmation',
                    content: `Are you sure you want to sign out of the ${coreState.entity.name} Portal?`,
                    okButtonProps: { style: { background: token.colorSuccess, color: 'white' } },
                    cancelButtonProps: { style: { background: token.colorError, color: 'white' } },
                    onOk: () => handleSignOut(),
                    okText: 'Yes',
                    cancelText: 'No',
                    className: token.themeControl
                  })
                } else {
                  if (coreState.tourOpen) {
                    dispatch(CoreReducer.actions.nextTourStep())
                  }
                  delete event.domEvent

                  if (warnUnsavedChanges()) {
                    Modal.confirm({
                      title: 'Careful, you have unsaved changes!',
                      content: 'Are you sure you want to leave this page? You have unsaved changes.',
                      okText: 'Leave Anyway',
                      cancelText: 'No, take me back',
                      okButtonProps: { style: { background: token.colorError, color: 'white' } },
                      onOk: () => {
                        dispatch(coreReducer.actions.setUnsavedChanges(false))
                        dispatch(CoreReducer.actions.menuItemClick(event))
                      }
                    })
                  } else {
                    dispatch(CoreReducer.actions.menuItemClick(event))
                  }
                }
              }}
              onOpenChange={() => {
                if (coreState.tourOpen) {
                  dispatch(CoreReducer.actions.nextTourStep())
                }
              }}
              defaultSelectedKeys={['home']}
              selectedKeys={handleCheckTabKey()}
              mode='inline'
              items={filterMenuItems()}
              style={{
                height: 'calc(100vh - 48px)',
                boxSizing: 'border-box',
                overflow: 'auto',
                position: 'fixed',
                left: 0,
                top: 0,
                width: sidebarWidth,
                transition: 'all 300ms ease',
                paddingTop: '64px'
              }}
            />
          </Sider>
        ) : undefined}

        <Content>
          <CustomRootContent />
        </Content>
      </Layout>
      {isMobile && isPatient() && coreState.mobNavBarEnabled && <MobNavBar />}
    </>
  )
}

export default TabNavigation
